import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import LimitedLegendaryIds from '@/static/limitedLegendaryIds.json'
import { useHeroStore } from './useHeroStore'
import { useExtensionStore } from './useExtensionStore'

export const useDeckTemplateStore = defineStore('deckTemplateStore', () => {
  const templates = ref<any>([])
  const accessories = ref<any>([])
  const bannedTemplates = ref<any>([])

  // getters
  const getDeckTemplates = computed(() => [...templates.value])
  const getDeckAccessories = computed(() => [...accessories.value])
  const getBannedTemplates = computed(() => [...bannedTemplates.value])

  const isSelectedTemplateRarityLimit = computed(() => (index, obj) => {
    const { heroRarityUpperLimitMap } = obj
    let template = templates.value[index].unitsList
    let selectedRarities = {
      common: 0,
      uncommon: 0,
      rare: 0,
      epic: 0,
      legendary: 0,
    }
    template.forEach(x => {
      if (useHeroStore().getHero(x.heroId).rarity == 1) selectedRarities.common++
      if (useHeroStore().getHero(x.heroId).rarity == 2) selectedRarities.uncommon++
      if (useHeroStore().getHero(x.heroId).rarity == 3) selectedRarities.rare++
      if (useHeroStore().getHero(x.heroId).rarity == 4) selectedRarities.epic++
      if (useHeroStore().getHero(x.heroId).rarity == 5) selectedRarities.legendary++
    })

    let check = false
    heroRarityUpperLimitMap.forEach(x => {
      if (x[1] != 0 && x[1] != 5) {
        if (x[0] == 1 && x[1] < selectedRarities.common) check = true
        if (x[0] == 2 && x[1] < selectedRarities.uncommon) check = true
        if (x[0] == 3 && x[1] < selectedRarities.rare) check = true
        if (x[0] == 4 && x[1] < selectedRarities.epic) check = true
        if (x[0] == 5 && x[1] < selectedRarities.legendary) check = true
      }
    })

    return check
  })

  const isSelectedTemplateAttrLimit = computed(() => (index, obj) => {
    const { heroAttributeLimitMap } = obj
    let template = templates.value[index].unitsList
    let selectedAttr = [0, 0, 0, 0, 0, 0]
    template.forEach(x => {
      if (useHeroStore().getHero(x.heroId).attribute == 1) selectedAttr[0]++
      if (useHeroStore().getHero(x.heroId).attribute == 2) selectedAttr[1]++
      if (useHeroStore().getHero(x.heroId).attribute == 3) selectedAttr[2]++
      if (useHeroStore().getHero(x.heroId).attribute == 4) selectedAttr[3]++
      if (useHeroStore().getHero(x.heroId).attribute == 5) selectedAttr[4]++
      if (useHeroStore().getHero(x.heroId).attribute == 6) selectedAttr[5]++
    })

    let check = false
    heroAttributeLimitMap.forEach(x => {
      if (x[0] == 1 && x[1] < selectedAttr[0]) check = true
      if (x[0] == 2 && x[1] < selectedAttr[1]) check = true
      if (x[0] == 3 && x[1] < selectedAttr[2]) check = true
      if (x[0] == 4 && x[1] < selectedAttr[3]) check = true
      if (x[0] == 5 && x[1] < selectedAttr[4]) check = true
      if (x[0] == 6 && x[1] < selectedAttr[5]) check = true
    })

    return check
  })

  const isSameHeroDisabled = computed(() => (index, obj) => {
    const { rules } = obj
    let party = templates.value[index].unitsList
    let check = false
    let heroTypes = []
    party.forEach(x => {
      let hero = useHeroStore().getHero(x.heroId)
      if (!heroTypes.includes(hero.heroType)) {
        heroTypes.push(hero.heroType)
      } else {
        check = true
      }
    })
    return rules.isNoSameHero && check
  })

  // actions
  async function fetchDeckTemplates(force = false) {
    if (!force && templates.value.length > 0) return

    const { flat, uniq } = this.$nuxt.$util
    const { $teamsService, $accessoryService } = this.$nuxt

    const res = await $teamsService.getMyDeckTemplates()
    const unitsLists = res.map(t => t.unitsList)
    const heroIds = flat(
      unitsLists.map(list => {
        return list.map(u => u.heroId)
      })
    )
    const extensionIds = flat(
      unitsLists.map(list => {
        return list.map(u => u.extensionIdsList)
      }),
      2
    )
    const accessoryIds = flat(
      unitsLists.map(list => {
        return list.map(u => u.accessoryId)
      })
    ).filter(a => a != 0)
    await useHeroStore().fetchHeroes(uniq(heroIds))
    await useExtensionStore().fetchExtensions(uniq(extensionIds))
    accessories.value = await $accessoryService.getAccessories(accessoryIds)
    templates.value = res
  }

  async function setDeckTemplate(index, data, name) {
    const { $teamsService } = this.$nuxt
    await $teamsService.setMyDeckTemplate(index, data, name)
    await this.fetchDeckTemplates(true)
  }

  async function fetchTemplateAssets(template, force = false) {
    const { flat, uniq } = this.$nuxt.$util
    const unitsList = template.unitsList || template.battleDeckUnitsList
    const heroIds = unitsList.map(u => u.heroId)
    const extensionIds = flat(unitsList.map(p => p.extensionIdsList))
    await Promise.all([
      useHeroStore().fetchHeroes(uniq(heroIds), force),
      useExtensionStore().fetchExtensions({
        ids: uniq(extensionIds),
        force,
      }),
    ])
  }

  // 該当イベントにおいて、レアリティが禁止されているものが含まれているテンプレートを取得
  function setBannedTemplates(bannedRarity: any, isLimitedLegendaryAllow = null) {
    const { uniq } = this.$nuxt.$util
    const {
      replicaHeroRaritiesList,
      heroRaritiesList,
      replicaExtensionRaritiesList,
      originalExtensionRaritiesList,
      accessoryRaritiesList,
    } = bannedRarity

    const bannedTemplateIds = []
    for (let i = 0; i < templates.value.length; i++) {
      for (let n = 0; n < templates.value[i].unitsList.length; n++) {
        const asset = templates.value[i].unitsList[n]
        if (asset.heroId) {
          let hero = useHeroStore().getHero(asset.heroId)
          if (!hero.asset && !!hero.rarity) {
            if (replicaHeroRaritiesList?.includes(hero.rarity)) {
              bannedTemplateIds.push(i)
              break
            }
          } else {
            if (heroRaritiesList?.includes(hero.rarity)) {
              if (!(LimitedLegendaryIds?.includes(hero.heroType) && isLimitedLegendaryAllow)) {
                bannedTemplateIds.push(i)
                break
              }
            } else if (
              isLimitedLegendaryAllow != null &&
              !isLimitedLegendaryAllow &&
              LimitedLegendaryIds?.includes(hero.heroType)
            ) {
              //LLユニットが禁止の場合、deck内のユニットtypeをlimitedLegendaryIdsに入っているかチェックする
              bannedTemplateIds.push(i)
              break
            }
          }
          if (asset.extensionIdsList[0]) {
            const extension = useExtensionStore().getExtension(asset.extensionIdsList[0])
            if (!extension.asset && !!extension.rarity) {
              if (replicaExtensionRaritiesList?.includes(extension.rarity)) {
                bannedTemplateIds.push(i)
                break
              }
            } else {
              if (originalExtensionRaritiesList?.includes(extension.rarity)) {
                bannedTemplateIds.push(i)
                break
              }
            }
          }
          if (asset.extensionIdsList[1]) {
            const extension = useExtensionStore().getExtension(asset.extensionIdsList[1])
            if (!extension.asset && !!extension.rarity) {
              if (replicaExtensionRaritiesList?.includes(extension.rarity)) {
                bannedTemplateIds.push(i)
                break
              }
            } else {
              if (originalExtensionRaritiesList?.includes(extension.rarity)) {
                bannedTemplateIds.push(i)
                break
              }
            }
          }
          if (asset.accessoryId) {
            const accessory = accessories.value.find(ac => ac.accessoryId == asset.accessoryId)
            if (!!accessory && accessoryRaritiesList?.includes(accessory.rarity)) {
              bannedTemplateIds.push(i)
              break
            }
          }
        }
      }
    }
    bannedTemplates.value = uniq(bannedTemplateIds)
  }

  function clearBannedTemplates() {
    bannedTemplates.value = []
  }

  return {
    templates,
    accessories,
    bannedTemplates,
    getDeckTemplates,
    getDeckAccessories,
    getBannedTemplates,
    isSelectedTemplateRarityLimit,
    isSelectedTemplateAttrLimit,
    isSameHeroDisabled,
    fetchDeckTemplates,
    setDeckTemplate,
    fetchTemplateAssets,
    setBannedTemplates,
    clearBannedTemplates,
  }
})
